import { Alert, Text, TextInput } from '@mantine/core';
import { IconInfoCircle, IconSearch, IconX } from '@tabler/icons-react';
import type { DataTableRowExpansionProps } from 'mantine-datatable';
import { useState } from 'react';

import { DownloadButton } from '#core/components/shared/DownloadButton.tsx';
import { recordsToCsv } from '#core/lib/transforms/records-to-csv.ts';

import { createGridComponent } from '../shared/helpers.ts';
import { ExpandedRowPanel } from './cells/ExpandedRowPanel.tsx';
import { TableSchema } from './schema.ts';
import classes from './Table.module.css';
import { TableRenderer } from './TableRenderer.tsx';
import { TableSkeleton } from './TableSkeleton.tsx';
import { filterRecords, transformToTableProps } from './utils.ts';

/**
 * Pure DashboardTable component for rendering tabular data.
 *
 * This is a pure renderer - receives all data as props, no internal data fetching.
 * Works in both dashboard grid and chat block contexts.
 *
 * Features:
 * - Client-side text filtering across all visible columns
 * - Automatic column type detection (boolean, date, number)
 * - Type-appropriate formatters (badges, dates, number formatting)
 * - Dot notation accessor support for nested paths
 * - Column pinning
 * - LLM-controlled column configuration
 * - Row expansion: click any row to expand and see full record details
 *
 * @example
 * ```tsx
 * <Table
 *   records={[{ name: 'Item 1', value: 100 }, { name: 'Item 2', value: 200 }]}
 *   columns={[{ accessor: 'name', title: 'Name' }, { accessor: 'value', title: 'Value' }]}
 *   title="My Table"
 * />
 * ```
 */
export const Table = createGridComponent(
  ({ records = [], columns, pinnedColumns, title }) => {
    const [filterText, setFilterText] = useState('');

    if (!records || records.length === 0) {
      return (
        <Alert color="blue" icon={<IconInfoCircle size={16} />} variant="light">
          No records to display
        </Alert>
      );
    }

    const filteredRecords = filterRecords(records, filterText);
    const tableProps = transformToTableProps({
      records: filteredRecords,
      columnConfig: columns,
      pinnedColumns,
      title,
    });
    const isFiltering = filterText.length > 0;

    // Row expansion configuration - shows full record details
    const rowExpansion: DataTableRowExpansionProps<Record<string, unknown>> = {
      allowMultiple: true,
      content: ({ record, collapse }) => <ExpandedRowPanel onCollapse={collapse} record={record} />,
    };

    return (
      <div className={classes.filterableTable}>
        <div className={classes.filterContainer}>
          <TextInput
            leftSection={<IconSearch size={16} />}
            onChange={(e) => setFilterText(e.currentTarget.value)}
            placeholder="Filter rows..."
            rightSection={
              filterText && (
                <IconX onClick={() => setFilterText('')} size={16} style={{ cursor: 'pointer' }} tabIndex={0} />
              )
            }
            size="xs"
            value={filterText}
            variant="unstyled"
          />
          {isFiltering && (
            <Text className={classes.filterCount}>
              {filteredRecords.length} of {records.length} rows
            </Text>
          )}
          <DownloadButton
            data={recordsToCsv(
              filteredRecords,
              tableProps.columns.map((c) => c.accessor),
            )}
            fileName={`${title ?? 'table-data'}.csv`}
            mimeType="text/csv"
            size="sm"
            tooltip="Download CSV"
            variant="subtle"
          />
        </div>
        <TableRenderer {...tableProps} rowExpansion={rowExpansion} />
      </div>
    );
  },
  TableSchema,
  TableSkeleton,
);
